import { getExhibitionKakaoTemplate } from '@bucketplace/common';
import { BREAK_POINT, MEDIA_QUERY } from '@bucketplace/design-system';
import { GridItemProps } from '@bucketplace/design-system/bds';
import { useScrollIntoAnchor } from '@bucketplace/jsx-editor/exhibition';
import styled from '@emotion/styled';
import React, { ReactElement, useMemo } from 'react';

import { cssColumn } from '@/styles/grids';

import { SectionStoreItems } from './SectionStoreItems';
import { SectionViewer } from './SectionViewer';

interface AnchorInfo {
  targetId?: string;
  isInitialAnchor: boolean;
}

interface PromotionsTemplateProps {
  children?: React.ReactNode;
  // 기획전 에디터 정보
  units: string;
  // promotion (기획전) id
  promotionId: string;
  // 기획전 타이틀
  title: string;
  // 기획전 커버이미지
  coverImageUrls: string[];
  // 기획전 > section > detail id
  detailId: string;
  // 앵커링 target, disable 정보
  anchorInfo: AnchorInfo;
  isLoading: boolean;
  className?: string;
  onViewerLoaded: (isLoaded: boolean) => void;
}

export function PromotionsTemplate({
  children,
  units,
  promotionId,
  title,
  coverImageUrls,
  detailId,
  anchorInfo,
  isLoading,
  className,
  onViewerLoaded,
}: PromotionsTemplateProps): ReactElement {
  const { containerRef, scrollToTarget, anchorEnd } = useScrollIntoAnchor({
    ...anchorInfo,
    isLoading,
    initialAnchor: anchorInfo.isInitialAnchor,
  });

  const kakaoTemplate = useMemo(
    () =>
      getExhibitionKakaoTemplate({
        exhibitionId: promotionId,
        title,
        coverImageUrls: coverImageUrls ?? [],
        products: [],
      }),
    [coverImageUrls, promotionId, title],
  );

  return (
    <Container
      ref={(ref) => {
        if (anchorEnd || !anchorInfo.isInitialAnchor) {
          containerRef.current = null;
        } else {
          containerRef.current = ref;
        }
      }}
      className={className}
    >
      <GridItem
        column={{
          mobile: 'full',
          tablet: 'full',
          laptop: 9,
        }}
      >
        <SectionViewer
          exhibitionId={promotionId}
          units={units}
          kakaoTemplate={kakaoTemplate}
          experimentType={null}
          onViewerLoaded={onViewerLoaded}
          onClick={scrollToTarget}
        />
        <SectionStoreItems id={promotionId} detailId={detailId} />
      </GridItem>

      {children}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: ${BREAK_POINT.desktop}px;
  margin: 0 auto;
  padding: 30px 60px;
  ${MEDIA_QUERY.max(
    'tablet',
    `
    padding: 0;
  `,
  )}
`;

const GridItem = styled.div<{ column: GridItemProps['column'] }>`
  position: relative;
  margin: 0 auto;
  ${({ column }) => cssColumn(column)};
`;
